/**
 * Safe popup helpers — used by the WO "View Executed WO" preview and
 * the proposal PDF preview popup. Both previously used
 * `popup.document.write("<html>...")` which is an XSS risk when any
 * portion of the HTML string contains user-controlled data
 * (wo_number, proposal title, attachment URL).
 *
 * This module builds the popup DOM with `createElement` +
 * `textContent` so no untrusted value is ever interpreted as HTML.
 *
 * Centralized in iter170 (Code Quality Report follow-up) to remove
 * the duplicated implementation across WorkOrderTab.jsx and
 * generateProposalPDF.js.
 */

/**
 * Open a new tab and render a single full-viewport iframe pointing at
 * `src`. The popup title is set via `textContent`. Returns `true` if
 * the popup opened successfully, `false` if the browser blocked it.
 *
 * @param {Object} opts
 * @param {string} opts.title  Document title for the popup tab.
 * @param {string} opts.src    URL or data: URI for the iframe.
 * @param {string} [opts.background="#f3f4f6"]  Background color CSS.
 */
export function openSafePdfPopup({ title, src, background = "#f3f4f6" }) {
  const popup = window.open("", "_blank");
  if (!popup) return false;
  const pdoc = popup.document;
  // Reset the document; safe boilerplate only, no untrusted values.
  pdoc.open();
  pdoc.write("<!doctype html><html><head></head><body></body></html>");
  pdoc.close();
  // Title — user-controlled string, so MUST go through textContent.
  const titleEl = pdoc.createElement("title");
  titleEl.textContent = title;
  pdoc.head.appendChild(titleEl);
  // Inline style — fixed string only, never derived from user input.
  const style = pdoc.createElement("style");
  style.textContent = `body{margin:0;display:flex;justify-content:center;background:${background}}iframe{border:none;width:100vw;height:100vh}`;
  pdoc.head.appendChild(style);
  // Iframe src — assigned as a property (not interpolated into HTML).
  const iframe = pdoc.createElement("iframe");
  iframe.src = src;
  pdoc.body.appendChild(iframe);
  return true;
}
